import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTimer } from "@/lib/TimerContext";
import { useTheme } from "@/lib/ThemeContext";
import { useMode } from "@/lib/ModeContext";
import { useAuth } from "@/lib/AuthContext";
import { useSubscription } from "@/lib/SubscriptionContext";
import { useCommandBarData } from "@/hooks/useCommandBarData";
import { api } from "@/lib/api";
import logger from "@/lib/logger";
import {
  MiniTimer, TimerControls, ModeMenu, LinkerDropdown, ExpandedStats,
  CommandBarNav, CommandBarActions,
} from "@/components/CommandBarParts";
import ReferralPanel from "@/components/ReferralPanel";
import NavTooltip from "@/components/NavTooltip";
import { Link2, Briefcase, Gamepad2, Leaf, Brain, Plus } from "lucide-react";

const BAR_KEY = "commandbar-expanded";
const DENSITY_KEY = "mmntm-density";
const MODE_ICONS = { work: Briefcase, game: Gamepad2, minimalist: Leaf, adhd: Brain };

export default function CommandBar() {
  const timer = useTimer();
  const { tokens: t, boxColors, styleType } = useTheme();
  const { mode, setMode } = useMode();
  const { logout, user } = useAuth();
  const { isPro } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const isGame = styleType === "game";
  const { liveStats, gameLevel, streak, todayFocus, byBox } = useCommandBarData(timer, boxColors);

  const [expanded, setExpanded] = useState(() => localStorage.getItem(BAR_KEY) === "true");
  const [showLinker, setShowLinker] = useState(false);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  // Bundle K — Migrate the stale `comfort` slug (pre-Bundle-K) to the
  // canonical `comfortable` so this toggle and the AppearanceSection
  // settings agree on the same value set. `mmntm-density` is also
  // migrated by AppearanceApplier, so this mirror is just for the
  // initial state read here. Read returns true ⇒ initial = compact.
  const [isCompact, setIsCompact] = useState(() => {
    try {
      const stored = localStorage.getItem(DENSITY_KEY);
      if (stored === "comfort") localStorage.setItem(DENSITY_KEY, "comfortable");
      return stored === "compact";
    } catch { return false; }
  });

  const isDashboard = location.pathname === "/";
  const isWorkstation = location.pathname === "/tasks";
  const isAnalytics = location.pathname === "/analytics";
  const isRecap = location.pathname === "/recap";

  useEffect(() => {
    // Bundle J/K — Compact Mode actually applies now via the existing
    // `[data-density="compact"]` and new `[data-density="spacious"]`
    // CSS rules in index.css. We use the canonical slug (`comfortable`,
    // not the legacy `comfort`) so the AppearanceSection settings and
    // this quick-toggle agree on the same value.
    //
    // We also persist the change via the Appearance API so toggling
    // here is reflected in the Settings → Appearance screen (and on
    // other devices). Fire-and-forget — failures don't block the UI.
    const slug = isCompact ? "compact" : "comfortable";
    document.documentElement.setAttribute("data-density", slug);
    document.documentElement.classList.toggle("compact-mode", isCompact);
    localStorage.setItem(DENSITY_KEY, slug);
    api.updateAppearance({ density: slug }).catch(() => {});
    try {
      window.dispatchEvent(new CustomEvent("mmntm:appearance-changed", { detail: { density: slug } }));
    } catch (e) { logger.warn("CommandBar: appearance-changed dispatch failed", e); }
  }, [isCompact]);

  useEffect(() => { localStorage.setItem(BAR_KEY, String(!!expanded)); }, [expanded]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (timer.status === "idle") timer.start();
        else if (timer.status === "running") timer.pause();
        else if (timer.status === "paused") timer.resume();
      } else if (e.key === "Escape") {
        if (timer.isActive) timer.stop();
      } else if (e.key === "1") timer.switchMode("focus");
      else if (e.key === "2") timer.switchMode("countdown");
      else if (e.key === "3") timer.switchMode("stopwatch");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [timer]);

  const modeColors = { focus: t.danger, countdown: t.accent3, stopwatch: t.accent2 };
  const modeColor = modeColors[timer.mode] || t.accent;
  const linkedBox = timer.boxes.find((b) => b.id === timer.linkedBoxId);
  const linkedColor = linkedBox ? (boxColors[linkedBox.color]?.text || t.accent) : null;
  const ModeIcon = MODE_ICONS[mode] || Gamepad2;

  // E-2.1 — publish the header's RENDERED height (nav row + stats strip +
  // chips) as --header-h so page content is offset by what is actually on
  // screen, in both the expanded and collapsed states. Falls back to the
  // CSS default (--topbar-height) until the first measurement lands.
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const publish = () => document.documentElement.style.setProperty("--header-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty("--header-h"); };
  }, []);

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-[60]" role="banner"
      style={{
        background: t.bgSecondary, borderBottom: `1px solid ${t.borderColor}`,
        boxShadow: timer.sessionFlash ? `0 0 20px ${modeColor}60, 0 1px 8px rgba(0,0,0,0.2)` : `0 1px 8px rgba(0,0,0,0.15)`,
      }} data-testid="command-bar">

      <div className="h-[var(--topbar-height)] flex items-center px-4 mx-auto" style={{ maxWidth: "1400px" }} data-testid="command-bar-compact">
        {/* LEFT: Logo + Nav */}
        <div className="flex items-center gap-2 shrink-0 mr-3">
          <NavTooltip label="MMNTM" description="Keyboard shortcuts: ?" shortcut="?">
            <div className="w-6 h-6 rounded flex items-center justify-center cursor-default" style={{ background: t.accent }}
              role="img" aria-label="MMNTM Home">
              <span className="text-[10px] font-black" style={{ color: t.bgPrimary, fontFamily: "'Outfit', sans-serif" }}>M</span>
            </div>
          </NavTooltip>
          <span className="text-[13px] font-bold tracking-tight hidden lg:block" style={{ color: t.textPrimary, fontFamily: "'Outfit', sans-serif" }}>MMNTM</span>
          <span className="hidden lg:inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ml-1" style={{ background: "#7C3AED", color: "#fff" }} data-testid="beta-badge">BETA</span>
          <div className="w-px h-5 hidden md:block ml-1" style={{ background: t.borderColor }} />
          <CommandBarNav isDashboard={isDashboard} isWorkstation={isWorkstation} isAnalytics={isAnalytics} isRecap={isRecap} navigate={navigate} t={t} />
          {isDashboard && (
            <NavTooltip label="New project">
              <button className="hidden sm:flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded transition-all ml-1"
                style={{ background: `${t.accent}12`, color: t.accent, border: `1px solid ${t.accent}20` }}
                onClick={() => window.dispatchEvent(new CustomEvent("mmntm:create-project"))} aria-label="New project" data-testid="create-project-btn">
                <Plus className="h-2.5 w-2.5" /> Project
              </button>
            </NavTooltip>
          )}
          {isWorkstation && (
            <NavTooltip label="New widget">
              <button className="hidden sm:flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded transition-all ml-1"
                style={{ border: `1px solid ${t.accent}30`, color: t.accent }}
                onClick={() => window.dispatchEvent(new CustomEvent("mmntm:add-widget"))} aria-label="New widget" data-testid="create-box-btn-header">
                <Plus className="h-2.5 w-2.5" /> Widget
              </button>
            </NavTooltip>
          )}
        </div>

        {/* CENTER: Timer + Mode + Controls */}
        <div className="flex items-center gap-2 justify-center flex-1 min-w-0">
          <MiniTimer timer={timer} modeColor={modeColor} t={t} isGame={isGame} />
          <span className="text-[15px] font-bold tabular-nums tracking-wide shrink-0"
            style={{ color: timer.isActive ? modeColor : t.textSecondary, fontFamily: "'JetBrains Mono', monospace",
              textShadow: timer.isActive && isGame && t.glowIntensity > 0 ? `0 0 8px ${modeColor}60` : "none" }}
            data-testid="command-timer-display">{timer.displayTime}</span>
          <div className="relative shrink-0">
            <button className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider transition-all"
              style={{ color: modeColor, border: `1px solid ${modeColor}25`, background: `${modeColor}08` }}
              onClick={() => { setShowModeMenu(!showModeMenu); setShowLinker(false); }} data-testid="mode-toggle-btn">
              <ModeIcon className="h-3 w-3" />
              <span className="hidden sm:inline">{timer.modeConfig?.label}</span>
              {timer.mode === "focus" && timer.status !== "break" ? <span>R{timer.focusRound}</span> : null}
            </button>
            <ModeMenu mode={mode} setMode={setMode} showModeMenu={showModeMenu} setShowModeMenu={setShowModeMenu} t={t} />
          </div>
          <TimerControls timer={timer} modeColor={modeColor} t={t} />
          <NavTooltip label="Link timer to project">
            <button className="p-1 rounded transition-all shrink-0" style={{ color: linkedBox ? linkedColor : `${t.textMuted}60` }}
              onClick={() => { setShowLinker(!showLinker); setShowModeMenu(false); }} aria-label="Link timer to project" data-testid="command-link-btn">
              <Link2 className="h-3 w-3" />
            </button>
          </NavTooltip>
          {linkedBox && (
            <span className="text-[10px] truncate max-w-[100px] hidden lg:block" style={{ color: linkedColor }}>{linkedBox.title}</span>
          )}
        </div>

        {/* RIGHT: Stats + Actions */}
        <CommandBarActions liveStats={liveStats} streak={streak} gameLevel={gameLevel} t={t}
          isPro={isPro} isCompact={isCompact} setIsCompact={setIsCompact}
          setShowReferral={setShowReferral} user={user} logout={logout}
          expanded={expanded} setExpanded={setExpanded} />
      </div>

      {showLinker && <LinkerDropdown timer={timer} boxColors={boxColors} t={t} onClose={() => setShowLinker(false)} />}

      <div className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: expanded ? "240px" : "0px", opacity: expanded ? 1 : 0 }} data-testid="command-bar-expanded">
        {expanded && <ExpandedStats timer={timer} liveStats={liveStats} todayFocus={todayFocus} streak={streak} byBox={byBox} boxColors={boxColors} t={t} />}
      </div>

      <ReferralPanel open={showReferral} onClose={() => setShowReferral(false)} />
    </header>
  );
}
